import { getShopKey } from '@dupe/common/helpers'
import type { ShoppingCategory } from '@dupe/common/types'
import { REVENUE_PROTECTED_CATEGORIES } from '@dupe/common/types'
import { decodeHTML } from 'entities'
import { usePostHog } from 'posthog-js/react'
import { useEffect, useMemo, useRef, useState } from 'react'
import type { BrainSeedDisplay } from '~/src/components/chats/BrainChat'
import type { ResultsResp } from '~/src/db/queryResultsResp'
import useGetDupesTwoPhase from '~/src/hooks/useGetDupesTwoPhase'
import useSelectedCountry from '~/src/hooks/useSelectedCountry'
import { productCategoryFromResult } from '~/src/lib/affiliateRouter'
import { seedProductsFailed } from '~/src/lib/brain/seedTurn'
import { DUPES_RESULT_PAGE_MATCH_LIMIT } from '~/src/lib/dupesResultLimits'
import { extractPriceFromResult, fixUrl } from '~/src/lib/helpers'

/**
 * True once PostHog's decide payload has landed (or after a fallback
 * timeout, so a blocked PostHog can't strand the fetch forever).
 *
 * `useGetDupes` keys its TanStack query on flag-derived values
 * (rankingVariant, scoringEnabled, includeEbay…) that all transition
 * undefined→value when flags resolve. A fetch started before that point
 * gets its key invalidated mid-flight and TanStack fires a second,
 * duplicate search — each one running the full paid pipeline (Lens +
 * vector search) server-side. Holding the fetch until flags settle keeps
 * the key stable so exactly one search runs.
 *
 * Uses `onFeatureFlags` (fires immediately when flags are already loaded,
 * else when the decide payload lands) — NOT `useActiveFeatureFlags`, which
 * returns [] rather than undefined for a fresh anonymous visitor and would
 * open the gate before flags actually resolve.
 */
function useFeatureFlagsSettled(timeoutMs = 2000): boolean {
  const posthog = usePostHog()
  const [settled, setSettled] = useState(false)
  useEffect(() => {
    const t = setTimeout(() => setSettled(true), timeoutMs)
    posthog?.onFeatureFlags?.(() => setSettled(true))
    return () => clearTimeout(t)
  }, [posthog, timeoutMs])
  return settled
}

/**
 * How long a MISSING country is allowed to read as "still loading".
 *
 * `useGeo` gives up after a hard 1500ms, so `useSelectedCountry` can report
 * `{isLoading: false, value: undefined}` — "resolved", with no country — while
 * `/api/geo` is still in flight. `useGetDupes` won't fetch without a country,
 * and a disabled TanStack query reports `isLoading: false`, so that window
 * otherwise reads as "searched, found nothing".
 *
 * It has to be BOUNDED. If geo fails outright, or answers with no country, and
 * there is no country cookie or `?country=`, the country never arrives at all —
 * an unbounded clause would leave the seed turn on a skeleton forever. After
 * this long we stop waiting and fall back to the previous behaviour, which is
 * wrong in a different way but at least terminates.
 */
const MISSING_COUNTRY_GRACE_MS = 6000

/**
 * True until `MISSING_COUNTRY_GRACE_MS` has elapsed since the seed went LIVE.
 *
 * ⚠️⚠️ SINCE IT WENT LIVE, NOT SINCE MOUNT, and the difference decides whether
 * the fix works at all. A caller can mount this hook long before it has
 * anything to seed from — `UnifiedChatsV2Entry` gates on `isFreshHashSeed`,
 * and `resultData` arrives from its own request. A window started at mount is
 * therefore already burning while nothing is waiting on it, and on a slow page
 * it can be spent before the search is even allowed to run. That would make
 * the clause a no-op in exactly the case it exists for: the slow one.
 *
 * ⚠️ IT ARMS ONCE AND IS NEVER RE-ARMED. Two failure modes, in opposite
 * directions, are being avoided here:
 *
 *   · Restarting it whenever `active` flips would let an oscillating caller
 *     extend the window indefinitely — an unbounded skeleton, which is the
 *     thing the bound exists to prevent.
 *   · Clearing it in an effect cleanup keyed on `active` would ORPHAN it: the
 *     cleanup cancels the pending timer, the guard then returns before arming
 *     a replacement, and `open` stays true for good. That is not hypothetical
 *     — it is the exact shape of the dead `loadingTimeout` in the extension's
 *     side panel, which never once fired in production.
 *
 * So the timer lives in a ref, is armed by a latched effect, and is cleared
 * only on unmount.
 */
export function useMissingCountryGrace(
  active: boolean,
  graceMs = MISSING_COUNTRY_GRACE_MS
): boolean {
  const [open, setOpen] = useState(true)
  const timer = useRef<ReturnType<typeof setTimeout> | undefined>(undefined)
  const started = useRef(false)

  // Unmount only — deliberately empty deps, so nothing below can orphan it.
  //
  // ⚠️ `started` is reset HERE and only here. React StrictMode (on in
  // `next.config.ts`) simulates mount → cleanup → remount on first mount
  // WITHOUT destroying the instance, so refs survive it. Clearing the timer
  // without clearing the latch left the arming effect's guard returning early
  // on the simulated remount, and the window never closed — the orphan this
  // design exists to prevent, reached through StrictMode instead of through an
  // `active`-keyed cleanup.
  //
  // Safe in both directions: an `active` oscillation never runs this cleanup
  // (empty deps), so it still cannot extend the window indefinitely; and on a
  // real unmount the ref is discarded anyway, so resetting it changes nothing
  // in production.
  useEffect(
    () => () => {
      clearTimeout(timer.current)
      started.current = false
    },
    []
  )

  useEffect(() => {
    if (!active || started.current) return
    started.current = true
    timer.current = setTimeout(() => setOpen(false), graceMs)
  }, [active, graceMs])

  return open
}

/**
 * The whole missing-country policy in one place: how long to wait, whether we
 * have given up, and what country to search with.
 *
 * ⚠️⚠️ THIS EXISTS BECAUSE THE DUPLICATION DRIFTED TWICE, in one PR. The SEO
 * product page (`pages/product/[...parts].tsx`) builds its own seed display
 * inline and makes its own fetch call, so every fix here had to be copied
 * there by hand — and twice it was copied only half way, leaving that page
 * honest about waiting but still ending in the old lie. A comment promising
 * the two were "kept identical" survived both.
 *
 * So the decision is a hook and the loading rule below is a function. Copying
 * a call is still possible; copying it WRONG is now much harder.
 */
export function useCountrylessFallback(
  selectedCountry: string | undefined,
  /** The seed is live — see {@link useMissingCountryGrace}. */
  active: boolean
): {
  graceOpen: boolean
  fellBack: boolean
  searchCountry: string | undefined
} {
  const graceOpen = useMissingCountryGrace(active)
  const fellBackRef = useRef(false)
  // One-way latch, so re-evaluating it with the same inputs is idempotent —
  // which is what makes the render-phase write safe under StrictMode's
  // double-invoke. See the PR discussion on `fellBackRef`.
  if (!selectedCountry && !graceOpen) fellBackRef.current = true
  return {
    graceOpen,
    fellBack: fellBackRef.current,
    // ⚠️ Pinned once we fall back: `selectedCountry` keys the TanStack query,
    // so a country arriving later would invalidate it and buy a second PAID
    // search for an answer the server already derived from the same IP.
    searchCountry: fellBackRef.current ? undefined : selectedCountry,
  }
}

/**
 * Does the seed turn still count as loading?
 *
 * Shared by both callers for the reason above. A missing country reads as
 * loading for the same reason unsettled flags do: the fetch has not been
 * enabled yet, and a DISABLED TanStack query reports `isLoading: false`, so
 * without this the seed reports zero products AND not-loading — which renders
 * as "no matches" for a search that never ran.
 */
export function seedProductsLoading({
  matchCount,
  flagsSettled,
  matchesLoading,
  selectedCountry,
  graceOpen,
}: {
  matchCount: number
  flagsSettled: boolean
  matchesLoading: boolean
  selectedCountry: string | undefined
  graceOpen: boolean
}): boolean {
  if (matchCount > 0) return false
  return !flagsSettled || matchesLoading || (!selectedCountry && graceOpen)
}

/**
 * Builds a `BrainSeedDisplay` from an already-loaded `results` row: the
 * source-product card plus its visual matches (fetched client-side via the
 * cached dupes pipeline). Feeding this into `BrainChat`'s `seedDisplay` prop
 * renders an instant local-only anchor turn — source card + results grid —
 * with **zero brain cost on mount**; the first user-typed prompt then fires
 * the real first turn. Used by both the deferred-LLM SEO product page
 * (`pages/product/[...parts]`) and the discover→v2 instant-resume entry
 * (`UnifiedChatsV2Entry`, fresh-hash branch).
 *
 * `options.enabled` gates the (expensive, paid) dupes fetch so callers that
 * mount the hook unconditionally — required by the rules of hooks when the
 * seed only applies on one render branch — don't fire a search on branches
 * that never show the seed (resume / text-prompt entries). Returns
 * `undefined` when disabled or when there's no result row to seed from.
 */
export default function useResultSeedDisplay(
  resultData: ResultsResp | null | undefined,
  options?: {
    enabled?: boolean
    /** Search with exactly this category instead of guessing one from the
     *  shop (`productCategoryFromResult`). `undefined` means "send none, let
     *  the API classify". Only the extension side panel passes it; leaving the
     *  key out keeps the current rule. */
    productCategory?: string
  }
): BrainSeedDisplay | undefined {
  const enabled = options?.enabled ?? true

  const { value: selectedCountry, isLoading: selectedCountryLoading } =
    useSelectedCountry()
  const flagsSettled = useFeatureFlagsSettled()
  // ⚠️ Keyed on the seed being LIVE, not on mount — see the hook. `enabled`
  // plus a result row is the earliest moment a search could run, so it is
  // the earliest moment the window should start spending itself.
  const {
    graceOpen: countryGraceOpen,
    fellBack,
    searchCountry,
  } = useCountrylessFallback(selectedCountry, enabled && !!resultData)

  const productTitle = decodeHTML(resultData?.title || '')
  const shopKey = resultData?.shopKey || getShopKey(resultData?.url)
  const mainImage = fixUrl(resultData?.image || resultData?.dataDetected?.image)
  // `in`, not `??`: the side panel passes `undefined` on purpose ("no
  // category"), which must not fall back to the shop guess.
  const productCategory =
    options && 'productCategory' in options
      ? options.productCategory
      : productCategoryFromResult(resultData)
  const { price } = extractPriceFromResult(resultData ?? null)

  const {
    matches: seedMatches,
    isLoading: seedMatchesLoading,
    fullFetched: seedMatchesFetched,
    category: resolvedCategory,
    error: seedMatchesError,
  } = useGetDupesTwoPhase(mainImage, {
    hash: resultData?.hash,
    q: productTitle,
    productCategory,
    selectedCountry: searchCountry,
    allowMissingCountry: fellBack,
    limit: DUPES_RESULT_PAGE_MATCH_LIMIT,
    // Always wait for the full response — skip the quick internal-only phase.
    // The quick phase paints results before the `non-furniture-relevance-sort`
    // variant (379352) resolves, so the seed grid rendered in the 4-col square
    // layout and then reflowed to 3-col masonry once the flag landed (a visible
    // flash on warm-cache discover clicks). Waiting for full means the arm is
    // resolved by first paint, so the grid renders in its final layout. Discover
    // dupes are near-always warm cache, so the quick head-start bought ~nothing.
    skipQuick: true,
    // Gate the paid fetch on the caller's branch, on flags settling, AND on the
    // seed inputs being stable — without a result row (`mainImage`/`hash` empty)
    // or while the country is still resolving, an early fire would search with
    // empty/undefined inputs and then refire (duplicate paid search + wrong
    // market) when those settle.
    enabled: enabled && !!resultData && !selectedCountryLoading && flagsSettled,
  })

  // Mirror the brain's `extractRailCategory`: surface the resolved category
  // only when it's revenue-protected (Furniture / Home Decor), so the seed turn
  // is classified exactly like a streamed furniture turn — which today means it
  // stays off the masonry layout. Written when that classification drove the
  // sponsored picks rail v2; that rail was deleted on 2026-09-16.
  const railCategory =
    resolvedCategory &&
    REVENUE_PROTECTED_CATEGORIES.has(resolvedCategory as ShoppingCategory)
      ? resolvedCategory
      : undefined

  return useMemo<BrainSeedDisplay | undefined>(() => {
    if (!enabled || !resultData) return undefined
    return {
      originalProduct: {
        name: productTitle,
        image: mainImage,
        link: resultData?.url,
        ...(price ? { price } : {}),
        source: shopKey,
      },
      products: seedMatches,
      // Before flags settle the fetch hasn't even been enabled yet — that
      // window must read as loading too, or the seed turn flashes its
      // "no matches" fallback for the first ~0-2s on every cold visit.
      //
      // A missing country is the same argument for a second window: the fetch
      // hasn't been enabled yet either, because `useGetDupes` only fetches
      // when `!!selectedCountry` and a DISABLED TanStack query reports
      // `isLoading: false`. Without this clause the seed reports zero products
      // AND not-loading, which `BrainChat` renders as "no matches" for a
      // search that never ran. Bounded by the grace window above, so a geo
      // failure degrades instead of spinning forever.
      // See src/hooks/seedLatch.race.spec.tsx.
      productsLoading: seedProductsLoading({
        matchCount: seedMatches.length,
        flagsSettled,
        matchesLoading: seedMatchesLoading,
        selectedCountry,
        graceOpen: countryGraceOpen,
      }),
      // ⛔ A failed search is not an empty one. The query runs with
      // `retry: false`, so before this the error was simply dropped and the
      // turn told the shopper we found nothing. See `seedTurn.ts`.
      productsFetched: seedMatchesFetched,
      productsFailed: seedProductsFailed({
        matchCount: seedMatches.length,
        error: seedMatchesError,
        matchesLoading: seedMatchesLoading,
      }),
      ...(railCategory ? { railCategory } : {}),
      // The FULL category, not just the revenue-protected slice above.
      // ⚠️ Load-bearing for the Marketplaces rail's dynamic pill set: it needs
      // to tell "Fashion" from "we don't know yet", and `railCategory` cannot —
      // it is `undefined` for fashion by construction. Without this the seed
      // turn would open with all three store pills and then narrow once the
      // dupes fetch lands. See docs/features/active/marketplaces/dynamic-pills-plan.md.
      ...(resolvedCategory ? { resolvedCategory } : {}),
    }
  }, [
    enabled,
    resultData,
    productTitle,
    mainImage,
    price,
    shopKey,
    seedMatches,
    flagsSettled,
    seedMatchesLoading,
    selectedCountry,
    countryGraceOpen,
    seedMatchesError,
    railCategory,
    resolvedCategory,
  ])
}
